import React from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { GuideCallout } from './GuideCallout';
import { GuideFigure } from './GuideFigure';
import type { GuideStepContent } from '../content/phantomContent';
import { GUIDE_ACCENT, guideTokens } from '../theme/guideTokens';

interface GuideStepProps {
  index: number;
  step: GuideStepContent;
  stepId?: string;
}

export const GuideStep: React.FC<GuideStepProps> = ({ index, step, stepId }) => {
  return (
    <Box
      component="li"
      id={stepId}
      sx={{
        display: 'flex',
        gap: 2,
        listStyle: 'none',
        mb: 3.5,
        scrollMarginTop: 120,
      }}
    >
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: guideTokens.fontDisplay,
          fontWeight: 700,
          fontSize: '0.875rem',
          color: '#fff',
          bgcolor: GUIDE_ACCENT,
          mt: 0.25,
        }}
      >
        {index}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: guideTokens.fontDisplay,
            fontWeight: 700,
            fontSize: '1.05rem',
            letterSpacing: '-0.02em',
            mb: 1,
          }}
        >
          {step.title}
        </Typography>

        <Stack spacing={1}>
          {step.body.map((paragraph) => (
            <Typography
              key={paragraph.slice(0, 40)}
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.65, fontFamily: guideTokens.fontBody }}
            >
              {paragraph}
            </Typography>
          ))}
        </Stack>

        {step.warnings && step.warnings.length > 0 && (
          <GuideCallout variant="warning" items={[...step.warnings]} />
        )}

        {step.tips && step.tips.length > 0 && (
          <GuideCallout variant="tip" items={[...step.tips]} />
        )}

        {step.link && (
          <Link
            href={step.link.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1.5,
              fontWeight: 600,
              fontSize: '0.875rem',
              fontFamily: guideTokens.fontDisplay,
              color: GUIDE_ACCENT,
            }}
          >
            {step.link.label}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        )}

        {step.image && (
          <GuideFigure src={step.image.src} alt={step.image.alt} caption={step.image.caption} />
        )}
      </Box>
    </Box>
  );
};
